import { Injectable } from "@angular/core";
import { sortBy } from "lodash";
import { BehaviorSubject, combineLatest, filter, forkJoin, interval, map, Observable, of, ReplaySubject, shareReplay, switchMap, take, takeUntil, tap } from "rxjs";
import { SpxLaunch, SpxLaunchTimings } from "../data/spx-launch";
import { CmsContentService } from "./cms-content.service";

const LAUNCH_SITE_TO_TIMEZONE_OFFSET = new Map([
  ["LC-39A, FLORIDA", "-05:00"],
  ["SLC-40, FLORIDA", "-05:00"],
  ["SLC-4E, CALIFORNIA", "-08:00"],
  ["STARBSE, TEXAS", "-06:00"],
  ["PAD 1, STARBASE", "-06:00"],
  ["PAD 2, STARBASE", "-06:00"],
  ["STARBASE PAD 1", "-06:00"],
  ["STARBASE PAD 2", "-06:00"],
  ["OMELEK ISLAND", "+12:00"]
]);
const LAUNCH_SITE_TO_DST_TIMEZONE_OFFSET = new Map([
  ["LC-39A, FLORIDA", "-04:00"],
  ["SLC-40, FLORIDA", "-04:00"],
  ["SLC-4E, CALIFORNIA", "-07:00"],
  ["STARBSE, TEXAS", "-05:00"],
  ["PAD 1, STARBASE", "-05:00"],
  ["PAD 2, STARBASE", "-05:00"],
  ["STARBASE PAD 1", "-05:00"],
  ["STARBASE PAD 2", "-05:00"],
  ["OMELEK ISLAND", "+12:00"]
]);

@Injectable({ providedIn: "root" })
export class MissionService {
  readonly launchTiles: BehaviorSubject<SpxLaunch[]> = new BehaviorSubject([]);
  readonly upcomingLaunchTiles: BehaviorSubject<SpxLaunch[]> = new BehaviorSubject([]);
  readonly upcomingLaunchTimings: BehaviorSubject<{ [id: string]: SpxLaunchTimings; }> = new BehaviorSubject({});
  readonly launchTiles$: Observable<SpxLaunch[]> = this.launchTiles.asObservable().pipe(shareReplay(1));
  readonly upcomingLaunchTiles$: Observable<SpxLaunch[]> = this.upcomingLaunchTiles.asObservable().pipe(shareReplay(1));
  readonly upcomingLaunchTimings$: Observable<{ [id: string]: SpxLaunchTimings; }> =
    this.upcomingLaunchTimings.asObservable().pipe(shareReplay(1));
  readonly upcomingLaunches$: Observable<SpxLaunch[]>;

  readonly maxThreshold: number = 86400;
  readonly maxInterval: number = 600000;
  readonly bustCacheIntervalThreshold = 30000;
  // Refresh every 10 minutes. At T-1 hour, refresh every minute. At T-2 minutes, refresh every 30 seconds.
  // At T-1 minute, refresh every 10 seconds. At T-20 seconds, refresh every 5 seconds.
  // At T-10 seconds, refresh every 2 seconds. At T-3 seconds, refresh every second.
  readonly refreshIntervals: any[] = [
    { "threshold": this.maxThreshold, "interval": this.maxInterval },
    { "threshold": 3600, "interval": 60000 },
    { "threshold": 120, "interval": 30000 },
    { "threshold": 60, "interval": 10000 },
    { "threshold": 20, "interval": 5000 },
    { "threshold": 10, "interval": 2000 },
    { "threshold": 3, "interval": 1000 },
  ];

  private readonly _destroy$ = new ReplaySubject<void>(1);
  private _fetchedAllTiles: boolean = false;
  private _nextRefresh: number = 0;
  private _processingRefresh: boolean = false;
  private _shouldBustCache: boolean = false;

  constructor(private cmsContentService: CmsContentService) {
    this.cmsContentService.getUpcomingLaunchTiles().pipe(take(1)).subscribe(
      tiles => this.upcomingLaunchTiles.next(tiles));
    this.cmsContentService.getUpcomingLaunchTimings().pipe(take(1)).subscribe(timings => {
      this.upcomingLaunchTimings.next(timings);
      this._setNextRefreshTime(timings);
      this._setupLaunchTimingRefresh();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  getUpcomingMissions(size: number = 10): Observable<SpxLaunch[]> {
    return combineLatest([this.upcomingLaunchTiles$, this.upcomingLaunchTimings$]).pipe(
      map(tileAndTimings => tileAndTimings[0]
        .filter(tile => tileAndTimings[1][tile.correlationId] != null)
        .map(tile => { tile.timings = tileAndTimings[1][tile.correlationId]; return tile; })
        .filter(tiles => tiles.timings.Order < size)),
      map((launches: SpxLaunch[]) => sortBy(launches, (mission) => mission.timings?.Order ?? 10)));
  }

  getOngoingMissions(): Observable<SpxLaunch[]> {
    this._fetchLaunchTilesIfNeeded();
    return this.launchTiles$.pipe(map(tiles => tiles.filter(tile => tile.missionStatus === "in-progress")));
  }

  getPastMissions(): Observable<SpxLaunch[]> {
    this._fetchLaunchTilesIfNeeded();
    return combineLatest([this.launchTiles$, this.upcomingLaunchTimings$]).pipe(
      map(tileAndTimings => tileAndTimings[0].filter(
        tile => tileAndTimings[1][tile.correlationId] == null && tile.missionStatus == "final")));
  }

  getTileForMission(id: string): Observable<SpxLaunch> {
    if (this.upcomingLaunchTiles.value.some(launch => launch.link == id)) {
      return combineLatest([this.upcomingLaunchTiles$, this.upcomingLaunchTimings$]).pipe(map(tileAndTimings => tileAndTimings[0]
        .filter(tile => tile.link == id)
        .map(tile => { tile.timings = tileAndTimings[1][tile.correlationId]; return tile; })[0]));
    }

    this._fetchLaunchTilesIfNeeded();
    return combineLatest([this.launchTiles$, this.upcomingLaunchTimings$]).pipe(map(tileAndTimings => tileAndTimings[0]
      .filter(tile => tile.link == id)
      .map(tile => { tile.timings = tileAndTimings[1][tile.correlationId]; return tile; })[0]));
  }

  /**
   * Converts a date string to a Date object with the appropriate timezone offset based on the launch site.
   * Handles DST automatically.
   * @param dateString - ISO 8601 formatted date string (e.g., "2026-05-19T14:30:00")
   * @param launchSite - The launch site name (e.g., "LC-39A, Florida")
   * @returns Date object with timezone offset applied
   */
  getLaunchTimeWithTimezone(dateString: string, launchSite: string): Date {
    const date: Date = new Date(dateString);
    const pad: string = launchSite.toUpperCase();

    return this._isDst(date) ?
      LAUNCH_SITE_TO_DST_TIMEZONE_OFFSET.has(pad) ?
        new Date(`${dateString}${LAUNCH_SITE_TO_DST_TIMEZONE_OFFSET.get(pad)}`)
        : date
      : LAUNCH_SITE_TO_TIMEZONE_OFFSET.has(pad) ?
        new Date(`${dateString}${LAUNCH_SITE_TO_TIMEZONE_OFFSET.get(pad)}`)
        : date;
  }

  private _setupLaunchTimingRefresh() {
    interval(1000).pipe(
      takeUntil(this._destroy$),
      // Only refresh if past next refresh datetime and not currently refreshing
      filter(_ => Date.now() >= this._nextRefresh && !this._processingRefresh),
      tap(_ => this._processingRefresh = true),
      switchMap(_ => this.cmsContentService.getUpcomingLaunchTimings(this._shouldBustCache)),
      switchMap(timings => {
        const oldUpcomingLaunchTimes = this.upcomingLaunchTimings.value;
        let launchTiles$: Observable<SpxLaunch[]> = of(null);
        let upcomingLaunchTiles$: Observable<SpxLaunch[]> = of(null);

        // Update launch tiles if the number of timings changed (there is a new launch or a launch moved to completed)
        if (oldUpcomingLaunchTimes && timings &&
          Object.keys(oldUpcomingLaunchTimes).length != Object.keys(timings).length) {
          launchTiles$ = this.cmsContentService.getLaunchTiles();
          upcomingLaunchTiles$ = this.cmsContentService.getUpcomingLaunchTiles();
        }

        return forkJoin({
          upcomingLaunchTimings: of(timings),
          launchTiles: launchTiles$,
          upcomingLaunchTiles: upcomingLaunchTiles$
        })
      }))
      .subscribe(state => {
        if (state.launchTiles) {
          this.launchTiles.next(state.launchTiles);
        }

        if (state.upcomingLaunchTiles) {
          this.upcomingLaunchTiles.next(state.upcomingLaunchTiles);
        }

        this.upcomingLaunchTimings.next(state.upcomingLaunchTimings);
        this._setNextRefreshTime(state.upcomingLaunchTimings);
        this._processingRefresh = false;
      });
  }

  private _fetchLaunchTilesIfNeeded() {
    if (!this._fetchedAllTiles) {
      this._fetchedAllTiles = true;
      this.cmsContentService.getLaunchTiles().pipe(take(1)).subscribe(tiles => this.launchTiles.next(tiles));
    }
  }

  private _setNextRefreshTime(upcomingLaunchTimings: { [id: string]: SpxLaunchTimings; }) {
    // Gets the minimum tzero value from the list of upcoming launches
    const minTime = Object.values(upcomingLaunchTimings)
      .map((timing: SpxLaunchTimings) => timing.TZeroPaused === true ? timing.TZeroValue :
        !!timing.TZeroLaunchDate?.Seconds ? Math.round(Date.now() / 1000) - timing.TZeroLaunchDate.Seconds :
          this.maxThreshold)
      .map((timingValue: number) => Math.abs(timingValue))
      .reduce((min, cur) => cur < min ? cur : min, this.maxThreshold);

    // Gets how many milliseconds to wait for a refresh based on the minimum tzero value
    const nextInterval: number = this.refreshIntervals
      .filter(interval => interval.threshold >= minTime)
      .reduce((minInterval, curInterval) =>
        curInterval.interval < minInterval ? curInterval.interval : minInterval, this.maxInterval);
    this._nextRefresh = Date.now() + nextInterval;

    // Bust cache if within 2 minutes of launch to avoid purging delay of CDN
    this._shouldBustCache = nextInterval <= this.bustCacheIntervalThreshold;
  }

  /**
   * Returns true when America/New_York is observing daylight saving time on the given date.
   * US launch sites (Florida, California, Texas) share the same federal DST schedule as New York,
   * so this is used to pick standard vs DST offsets for all continental US pads.
   */
  private _isDst(date: Date): boolean {
    const year: number = date.getUTCFullYear();
    // January is always standard time; July is always DST for America/New_York.
    const january: Date = new Date(Date.UTC(year, 0, 1));
    const july: Date = new Date(Date.UTC(year, 6, 1));
    const dateOffset: number = this._getTimeZoneOffsetMinutes(date, 'America/New_York');
    const standardOffset: number = Math.max(
      this._getTimeZoneOffsetMinutes(january, 'America/New_York'),
      this._getTimeZoneOffsetMinutes(july, 'America/New_York'));

    // getTimezoneOffset-style values: larger = further west of UTC (e.g. EST 300, EDT 240).
    return dateOffset < standardOffset;
  }

  /**
   * Offset in minutes to add to the zoned local time to get UTC (same sense as Date.getTimezoneOffset()).
   * e.g. America/New_York is 300 in EST and 240 in EDT.
   */
  private _getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
    const parts: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const value = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find(part => part.type === type)?.value);

    // Some engines report midnight as hour "24".
    const hour: number = value('hour') % 24;
    const asUtc: number = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      hour,
      value('minute'),
      value('second'));

    // Same convention as Date.getTimezoneOffset(): minutes to add to local to get UTC
    // (positive west of UTC — e.g. EST 300, EDT 240).
    return (date.getTime() - asUtc) / 60000;
  }
}